/*
 * Vehicle Matcher (v1)
 *
 * Custom element <vehicle-matcher> that reads product JSON, builds
 * cascading Make -> Model -> Year dropdowns, and renders matching product
 * cards with the theme's standard product-card snippet. Falls through to
 * email capture (POST to internal-tools) when the vehicle isn't covered.
 *
 * Tesla-screen rule: a Tesla-style product is only shown when no Module or
 * Premium HU matches the same vehicle.
 */

if (!customElements.get('vehicle-matcher')) {
  // Toggle for the no-result state. 'A' = email capture, 'B' = universal
  // head-unit recommendation. Code-level switch only. B ran 05 Jul to
  // 08 Oct 2026 and stopped all email capture, so A is back on.
  const NO_RESULT_VARIANT = 'A';
  const vehicleMatcherDataCache = new Map();
  const vehicleMatcherShownMemory = new Set();

  customElements.define(
    'vehicle-matcher',
    class VehicleMatcher extends HTMLElement {
      constructor() {
        super();
        this.products = [];
        this.makeIndex = new Map(); // make -> Set<model>
        this.byMakeModel = new Map(); // `${make}::${model}` -> Product[]
        this.cardCache = new Map(); // `${handle}::${variantId}` -> rendered card HTML
        this.maxCardCacheEntries = 80;
        this.yearRange = { min: 2001, max: 2026 };
        this.otherValue = '__other__';
        this.renderToken = 0;
        this.lastSelectInteraction = null;
        this.dataReady = false;
        this.dataLoadPromise = null;
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.makeEl = this.querySelector('[data-vm-make]');
        this.modelEl = this.querySelector('[data-vm-model]');
        this.yearEl = this.querySelector('[data-vm-year]');
        this.resultsEl = this.querySelector('[data-vm-results]');
        this.emptyEl = this.querySelector('[data-vm-empty]');
        this.emptyTitleEl = this.querySelector('[data-vm-empty-title]');
        this.emptyEyebrowEl = this.querySelector('[data-vm-empty-eyebrow]');
        this.emptySubtitleEl = this.querySelector('[data-vm-empty-subtitle]');
        this.emailPanelEl = this.querySelector('[data-vm-email-panel]');
        this.emailFormEl = this.querySelector('[data-vm-email-form]');
        this.emailStatusEl = this.querySelector('[data-vm-email-status]');
        this.universalEl = this.querySelector('[data-vm-universal]');
        this.captureUniversalVideoFacades();
        this.manualFieldsEl = this.querySelector('[data-vm-manual-fields]');
        this.manualVehicleInputEl = this.emailFormEl?.querySelector('input[name="manual_vehicle"]');
        this.manualPrefixEl = this.querySelector('[data-vm-manual-prefix]');
        this.manualLabelEl = this.querySelector('[data-vm-manual-label]');
        this.defaultEmptyTitle = this.emptyTitleEl?.textContent || '';
        this.defaultEmptySubtitle = this.emptySubtitleEl?.textContent || '';
        this.stepEls = this.querySelectorAll('[data-vm-step]');
        this.fieldEls = this.querySelectorAll('[data-vm-field]');
        this.apiUrl = this.getAttribute('data-api-url') || '';
        this.dataUrl = this.getAttribute('data-data-url') || '';
        this.lazyData = this.getAttribute('data-lazy-data') === 'true';

        this.selectEls = [this.makeEl, this.modelEl, this.yearEl].filter(Boolean);
        this.selectEls.forEach((select) => this.bindSelectInteraction(select));

        this.makeEl.addEventListener('change', () => {
          this.onMakeChange();
        });
        this.modelEl.addEventListener('change', () => {
          this.onModelChange();
        });
        this.yearEl.addEventListener('change', () => {
          this.onYearChange();
        });
        if (this.emailFormEl) {
          this.emailFormEl.addEventListener('submit', (e) => this.onEmailSubmit(e));
        }
        if (this.resultsEl) {
          this.resultsEl.addEventListener('click', (event) => this.onResultClick(event));
        }
        if (this.universalEl) {
          this.universalEl.addEventListener('click', (event) => this.onUniversalClick(event));
        }

        this.updateStepState();
        this._onAnalyticsConsent = () => this.trackMatcherShown();
        document.addEventListener('cts:analytics-consent-enabled', this._onAnalyticsConsent);
        this.trackMatcherShown();
        if (!this.lazyData) this.loadData();
      }

      disconnectedCallback() {
        document.removeEventListener('cts:analytics-consent-enabled', this._onAnalyticsConsent);
        // Remove the pending MATCHER_SHOWN listener so a disconnected instance
        // (theme-editor / Section Rendering re-render) can't leak it or fire late.
        if (this._onCaptureEnabled) {
          document.removeEventListener('cts:posthog-capture-enabled', this._onCaptureEnabled);
          this._onCaptureEnabled = null;
        }
      }

      trackMatcherShown() {
        this.mirrorToClarity('MATCHER_SHOWN');
        const sectionId = this.dataset.sectionId || 'unknown';
        const storageKey = `__ph_matcher_shown_${sectionId}`;
        let useMemoryDedupe = false;

        // True if this section's impression was already recorded this session.
        // Checked at entry AND again right before a deferred fire, so two
        // instances sharing a sectionId (e.g. a theme-editor re-render) can't
        // both emit MATCHER_SHOWN when capture finally enables.
        const alreadyShown = () => {
          if (useMemoryDedupe) return vehicleMatcherShownMemory.has(storageKey);
          try {
            return !!window.sessionStorage[storageKey];
          } catch (error) {
            return vehicleMatcherShownMemory.has(storageKey);
          }
        };

        try {
          if (!window.sessionStorage) throw new Error('sessionStorage unavailable');
          if (window.sessionStorage[storageKey]) return;
        } catch (error) {
          useMemoryDedupe = true;
          if (vehicleMatcherShownMemory.has(storageKey)) return;
        }

        const markShown = () => {
          if (useMemoryDedupe) {
            vehicleMatcherShownMemory.add(storageKey);
            return;
          }
          try {
            window.sessionStorage[storageKey] = '1';
          } catch (error) {
            vehicleMatcherShownMemory.add(storageKey);
          }
        };

        const fireOnce = () => {
          if (alreadyShown()) return true;
          const tracked = this.track('MATCHER_SHOWN', {
            section_id: this.dataset.sectionId || null,
          });
          if (tracked) markShown();
          return tracked;
        };

        // Fire immediately when capture is already enabled.
        if (fireOnce()) return;

        // Capture is enabled but the fire still failed (consent denied or
        // PostHog unavailable) — retrying will not help, so bail.
        if (window.__ctsPostHogCaptureEnabled === true) return;

        // Otherwise the PostHog loader is still initialising (it boots lazily on
        // an idle callback). MATCHER_SHOWN fires here in connectedCallback, often
        // before capture is enabled, and the loader stub drops pre-enable
        // captures — which silently lost ~93% of impressions. Defer the
        // impression once until capture is enabled, then fire it. The listener is
        // stored on the instance and removed in disconnectedCallback so a section
        // re-render can't leak it or fire from a detached element.
        if (this._onCaptureEnabled) return;
        this._onCaptureEnabled = () => {
          document.removeEventListener('cts:posthog-capture-enabled', this._onCaptureEnabled);
          this._onCaptureEnabled = null;
          if (!this.isConnected) return;
          fireOnce();
        };
        document.addEventListener('cts:posthog-capture-enabled', this._onCaptureEnabled);
      }

      track(name, props = {}, options = undefined) {
        if (!name) return false;
        this.mirrorToClarity(name);
        if (window.__ctsPostHogCaptureEnabled !== true) return false;

        try {
          const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
          if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') return false;
          if (!customerPrivacy.analyticsProcessingAllowed()) return false;
        } catch (error) {
          return false;
        }

        if (!window.posthog || typeof window.posthog.capture !== 'function') return false;

        try {
          window.posthog.capture(name, props, options);
          return true;
        } catch (error) {
          console.warn('[vehicle-matcher] PostHog capture failed', error);
          return false;
        }
      }

      mirrorToClarity(name) {
        if (typeof window.ctsTrackClarityEvent !== 'function') return;
        const key = `__clarity_matcher_shown_${this.dataset.sectionId || 'unknown'}`;
        if (name === 'MATCHER_SHOWN') {
          if (vehicleMatcherShownMemory.has(key)) return;
          try {
            if (window.sessionStorage.getItem(key)) return;
          } catch (error) { /* Use the in-memory fallback. */ }
        }
        if (window.ctsTrackClarityEvent(name) && name === 'MATCHER_SHOWN') {
          vehicleMatcherShownMemory.add(key);
          try { window.sessionStorage.setItem(key, '1'); } catch (error) { /* In-memory fallback. */ }
        }
      }

      async loadData(options = {}) {
        if (this.dataReady) return true;
        if (this.dataLoadPromise) return this.dataLoadPromise;

        this.dataLoadPromise = this.loadDataForInstance(options).finally(() => {
          this.dataLoadPromise = null;
        });
        return this.dataLoadPromise;
      }

      async loadDataForInstance(options = {}) {
        if (!this.dataUrl) {
          console.error('[vehicle-matcher] Missing data-data-url');
          this.setMakePlaceholder('Data unavailable');
          return false;
        }

        const selectedMake = this.makeEl.value;
        const refreshMakeOptions =
          options.refreshMakeOptions ??
          (!this.lazyData || this.makeEl.options.length <= 1);

        try {
          const data = await this.fetchVehicleData(this.dataUrl);
          this.products = data.products;
          this.makeIndex = data.makeIndex;
          this.byMakeModel = data.byMakeModel;
          this.yearRange = data.yearRange;
          this.dataReady = true;

          if (refreshMakeOptions) {
            this.populateMakeOptions(options.preserveMake ? selectedMake : this.makeEl.value);
          }
        } catch (e) {
          console.error('[vehicle-matcher] Failed to fetch data', e);
          this.products = [];
          this.makeIndex = new Map();
          this.byMakeModel = new Map();
          this.dataReady = false;
          this.resetModel();
          this.resetYear();
          this.setMakePlaceholder('Data unavailable. Refresh to retry');
          return false;
        }

        if (this.makeEl.disabled) this.makeEl.disabled = false;
        if (refreshMakeOptions || selectedMake) this.updateStepState();
        return true;
      }

      async fetchVehicleData(dataUrl) {
        if (vehicleMatcherDataCache.has(dataUrl)) {
          return vehicleMatcherDataCache.get(dataUrl);
        }

        const promise = this.fetchWithTimeout(dataUrl)
          .then(async (res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const payload = await res.json();
            const products = Array.isArray(payload)
              ? payload
              : Array.isArray(payload?.products)
                ? payload.products
                : [];
            const indexes = this.buildProductIndexes(products);

            return {
              products,
              yearRange: this.resolveYearRange(payload, products),
              makeIndex: indexes.makeIndex,
              byMakeModel: indexes.byMakeModel,
            };
          })
          .catch((error) => {
            vehicleMatcherDataCache.delete(dataUrl);
            throw error;
          });

        vehicleMatcherDataCache.set(dataUrl, promise);
        return promise;
      }

      setMakePlaceholder(text) {
        if (!this.makeEl) return;
        const first = this.makeEl.querySelector('option');
        if (first && first.value === '') {
          first.textContent = text;
          first.selected = true;
          first.disabled = true;
          first.hidden = true;
        }
      }

      indexProducts() {
        const indexes = this.buildProductIndexes(this.products);
        this.makeIndex = indexes.makeIndex;
        this.byMakeModel = indexes.byMakeModel;
      }

      buildProductIndexes(products) {
        const makeIndex = new Map();
        const byMakeModel = new Map();

        for (const p of products) {
          if (!p.make || !p.model) continue;
          if (!makeIndex.has(p.make)) makeIndex.set(p.make, new Set());
          makeIndex.get(p.make).add(p.model);
          const key = this.key(p.make, p.model);
          if (!byMakeModel.has(key)) byMakeModel.set(key, []);
          byMakeModel.get(key).push(p);
        }

        return { makeIndex, byMakeModel };
      }

      key(make, model) {
        return `${make}::${model}`;
      }

      resolveYearRange(payload, products = this.products) {
        const configuredMin =
          payload?.year_range?.min == null ? NaN : Number(payload.year_range.min);
        const configuredMax =
          payload?.year_range?.max == null ? NaN : Number(payload.year_range.max);
        if (Number.isFinite(configuredMin) && Number.isFinite(configuredMax)) {
          return {
            min: Math.trunc(configuredMin),
            max: Math.max(Math.trunc(configuredMax), Math.trunc(configuredMin)),
          };
        }

        let min = Infinity;
        for (const product of products) {
          for (const year of Object.keys(product.year_variants || {})) {
            const parsed = Number(year);
            if (Number.isFinite(parsed) && parsed < min) min = parsed;
          }
        }

        return {
          min: Number.isFinite(min) ? min : 2001,
          max: 2026,
        };
      }

      availableYearOptions(make, model) {
        const years = new Set();
        const products = this.byMakeModel.get(this.key(make, model)) || [];

        for (const product of products) {
          for (const year of Object.keys(product.year_variants || {})) {
            if (/^\d{4}$/.test(year)) years.add(year);
          }
        }

        return Array.from(years).sort((a, b) => Number(b) - Number(a));
      }

      categorize(product) {
        const s = [
          product?.product_type,
          product?.title,
          product?.handle,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .replace(/[-_]+/g, ' ');

        if (/\bmodule\b/.test(s)) return 'module';
        if (s.includes('head unit') || /\bhu\b/.test(s)) return 'hu';
        if (/\btesla\b/.test(s)) return 'tesla';
        return 'other';
      }

      populateMakeOptions(selectedMake = '') {
        const makes = Array.from(this.makeIndex.keys()).sort((a, b) =>
          a.localeCompare(b),
        );
        const placeholder =
          this.makeEl.dataset.vmPlaceholder ||
          this.makeEl.querySelector('option[value=""]')?.textContent?.trim() ||
          'Select make';

        this.makeEl.innerHTML = '';
        const first = document.createElement('option');
        first.value = '';
        first.textContent = placeholder;
        first.selected = true;
        first.disabled = true;
        first.hidden = true;
        this.makeEl.appendChild(first);

        for (const make of makes) {
          const opt = document.createElement('option');
          opt.value = make;
          opt.textContent = make;
          this.makeEl.appendChild(opt);
        }
        this.appendOtherOption(this.makeEl);

        if (selectedMake) {
          this.makeEl.value = Array.from(this.makeEl.options).some((option) => option.value === selectedMake)
            ? selectedMake
            : '';
        }
      }

      bindSelectInteraction(select) {
        select.addEventListener('pointerdown', (event) => {
          this.clearAutoAdvancedFocus(select);
          this.lastSelectInteraction =
            event.pointerType === 'mouse' || event.pointerType === 'pen'
              ? 'fine-pointer'
              : 'touch';
        });
        select.addEventListener('mousedown', () => {
          this.clearAutoAdvancedFocus(select);
          if (!('PointerEvent' in window)) this.lastSelectInteraction = 'fine-pointer';
        });
        select.addEventListener(
          'touchstart',
          () => {
            this.clearAutoAdvancedFocus(select);
            this.lastSelectInteraction = 'touch';
          },
          { passive: true },
        );
        select.addEventListener('keydown', () => {
          this.clearAutoAdvancedFocus(select);
          this.lastSelectInteraction = 'keyboard';
        });
        select.addEventListener('blur', () => this.clearAutoAdvancedFocus(select));
      }

      async onMakeChange() {
        const token = this.cancelPendingRender();
        this.resetModel();
        this.resetYear();
        this.hideResults();
        this.hideEmpty();

        const make = this.makeEl.value;
        if (!make) {
          this.updateStepState();
          return;
        }
        this.track('MATCHER_MAKE_SELECTED', {
          make: this.analyticsValue(make),
        });
        if (this.isOtherValue(make)) {
          this.showEmpty();
          this.updateStepState();
          return;
        }

        if (!this.dataReady) {
          this.resetSelectPlaceholder(this.modelEl, 'Loading models...');
          this.modelEl.disabled = true;
          const loaded = await this.loadData({ preserveMake: true, refreshMakeOptions: false });
          if (!loaded || token !== this.renderToken) return;

          this.resetModel('Select model');
          this.resetYear();
        }

        const readyMake = this.makeEl.value;
        if (!readyMake || this.isOtherValue(readyMake)) {
          this.updateStepState();
          return;
        }

        const models = Array.from(this.makeIndex.get(readyMake) || []).sort((a, b) =>
          a.localeCompare(b),
        );
        for (const model of models) {
          const opt = document.createElement('option');
          opt.value = model;
          opt.textContent = model;
          this.modelEl.appendChild(opt);
        }
        this.appendOtherOption(this.modelEl);
        this.modelEl.disabled = false;
        this.updateStepState();
        this.advanceToSelect(this.modelEl);
      }

      onModelChange() {
        this.cancelPendingRender();
        this.resetYear();
        this.hideResults();
        this.hideEmpty();

        const make = this.makeEl.value;
        const model = this.modelEl.value;
        if (!make || !model) {
          this.updateStepState();
          return;
        }
        this.track('MATCHER_MODEL_SELECTED', {
          make: this.analyticsValue(make),
          model: this.analyticsValue(model),
        });
        if (this.isOtherValue(model)) {
          this.showEmpty();
          this.updateStepState();
          return;
        }

        const sorted = this.availableYearOptions(make, model);
        for (const y of sorted) {
          const opt = document.createElement('option');
          opt.value = y;
          opt.textContent = y;
          this.yearEl.appendChild(opt);
        }
        this.appendOtherOption(this.yearEl);
        this.yearEl.disabled = false;
        this.updateStepState();
        this.advanceToSelect(this.yearEl);
      }

      onYearChange() {
        const token = this.cancelPendingRender();
        this.hideResults();
        this.hideEmpty();
        this.updateStepState();

        const make = this.makeEl.value;
        const model = this.modelEl.value;
        const year = this.yearEl.value;
        if (!make || !model || !year) return;
        const analyticsMake = this.analyticsValue(make);
        const analyticsModel = this.analyticsValue(model);
        const analyticsYear = this.analyticsValue(year);

        if (this.isOtherValue(year)) {
          this.track('MATCHER_YEAR_SELECTED', {
            make: analyticsMake,
            model: analyticsModel,
            year: analyticsYear,
            result_count: 0,
            category_breakdown: this.emptyCategoryBreakdown(),
          });
          this.showEmpty(token);
          return;
        }

        const candidates = (this.byMakeModel.get(this.key(make, model)) || []).filter(
          (p) => p.year_variants && p.year_variants[year],
        );

        if (candidates.length === 0) {
          this.track('MATCHER_YEAR_SELECTED', {
            make: analyticsMake,
            model: analyticsModel,
            year: analyticsYear,
            result_count: 0,
            category_breakdown: this.emptyCategoryBreakdown(),
          });
          this.showEmpty(token);
          return;
        }

        const buckets = { module: [], hu: [], tesla: [], other: [] };
        for (const p of candidates) {
          const cat = this.categorize(p);
          buckets[cat].push({
            ...p,
            _cat: cat,
            _variant_id: this.variantIdForYear(p, year),
          });
        }
        const hasPriority = buckets.module.length > 0 || buckets.hu.length > 0;
        const shown = [
          ...buckets.module,
          ...buckets.hu,
          ...(hasPriority ? [] : buckets.tesla),
          ...buckets.other,
        ];

        if (shown.length === 0) {
          this.track('MATCHER_YEAR_SELECTED', {
            make: analyticsMake,
            model: analyticsModel,
            year: analyticsYear,
            result_count: 0,
            category_breakdown: this.emptyCategoryBreakdown(),
          });
          this.showEmpty(token);
          return;
        }

        this.track('MATCHER_YEAR_SELECTED', {
          make: analyticsMake,
          model: analyticsModel,
          year: analyticsYear,
          result_count: shown.length,
          category_breakdown: this.categoryBreakdown(shown),
        });
        this.renderResults(shown, token);
      }

      updateStepState() {
        if (!this.makeEl || !this.modelEl || !this.yearEl) return;

        const makeSelected = Boolean(this.makeEl.value);
        const modelSelected = Boolean(this.modelEl.value);
        const yearSelected = Boolean(this.yearEl.value);
        const makeIsOther = this.isOtherValue(this.makeEl.value);
        const modelIsOther = this.isOtherValue(this.modelEl.value);
        const yearIsOther = this.isOtherValue(this.yearEl.value);
        const makeHasRealSelection = makeSelected && !makeIsOther;
        const states = {
          make: makeSelected ? 'complete' : 'active',
          model: makeIsOther ? 'pending' : modelSelected ? 'complete' : makeSelected ? 'active' : 'pending',
          year:
            makeIsOther || modelIsOther
              ? 'pending'
              : yearSelected
                ? 'complete'
                : modelSelected
                  ? 'active'
                  : 'pending',
        };
        const progress = yearIsOther || modelSelected ? 1 : makeSelected && !makeIsOther ? 0.5 : 0;

        this.classList.toggle('vehicle-matcher--make-selected', makeHasRealSelection);
        this.style.setProperty('--vm-step-progress', String(progress));
        this.stepEls.forEach((step) => {
          const state = states[step.dataset.vmStep] || 'pending';
          step.classList.toggle('is-active', state === 'active');
          step.classList.toggle('is-complete', state === 'complete');
          step.classList.toggle('is-pending', state === 'pending');
        });
        this.fieldEls.forEach((field) => {
          const state = states[field.dataset.vmField] || 'pending';
          field.classList.toggle('is-active', state === 'active');
          field.classList.toggle('is-complete', state === 'complete');
          field.classList.toggle('is-pending', state === 'pending');
        });
      }

      async renderResults(items, token = this.renderToken) {
        if (token !== this.renderToken) return;

        this.resultsEl.hidden = false;
        this.resultsEl.innerHTML = '<p class="vehicle-matcher__loading">Loading matching products...</p>';
        this.hideEmpty();
        // Anchor the viewport now so the cards fill in at a position the user
        // can see, rather than swapping in below the fold.
        this.scrollToReveal(this.resultsEl);

        const grid = document.createElement('ul');
        grid.className =
          'vehicle-matcher__grid grid product-grid contains-card contains-card--product contains-card--standard grid--4-col-desktop grid--2-col-tablet-down';
        grid.setAttribute('role', 'list');
        const renderedItems = [];

        try {
          const cards = await this.mapWithConcurrency(items, 4, async (item, index) => {
            try {
              const card = await this.renderThemeCard(item, index);
              return card ? { card, item } : null;
            } catch (err) {
              console.error('[vehicle-matcher] Failed to render product card', item?.handle, err);
              return null;
            }
          });
          if (token !== this.renderToken || !this.isConnected) return;
          for (const result of cards) {
            if (result?.card) {
              grid.appendChild(result.card);
              renderedItems.push(result.item);
            }
          }
        } catch (err) {
          console.error('[vehicle-matcher] Failed to render theme cards', err);
        }

        if (!grid.children.length) {
          if (token !== this.renderToken) return;
          this.classList.remove('vehicle-matcher--has-results');
          this.resultsEl.innerHTML =
            '<p class="vehicle-matcher__loading">Matching products found, but the product cards could not load. Refresh and try again.</p>';
          return;
        }

        this.classList.add('vehicle-matcher--has-results');
        this.resultsEl.innerHTML = '';
        this.resultsEl.appendChild(grid);
        this.resultsEl.hidden = false;
        this.hideEmpty();
        const selection = this.currentVehicleSelection();
        // result_count is part of the key so a re-render of the same vehicle
        // with a different count (e.g. a card fetch failed first time) still
        // reports the corrected numbers instead of being deduped away.
        const resultsKey = `${selection.make}|${selection.model}|${selection.year}|${renderedItems.length}`;
        this._resultsShownKeys = this._resultsShownKeys || new Set();
        if (!this._resultsShownKeys.has(resultsKey)) {
          const tracked = this.track('MATCHER_RESULTS_SHOWN', {
            ...selection,
            result_count: renderedItems.length,
            category_breakdown: this.categoryBreakdown(renderedItems),
          });
          // Only mark deduped on a successful fire, so a first impression that
          // failed (capture not yet enabled) can still be recorded on retry.
          if (tracked) this._resultsShownKeys.add(resultsKey);
        }
      }

      fetchWithTimeout(resource, options = {}, timeoutMs = 8000) {
        if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
          return fetch(resource, {
            ...options,
            signal: AbortSignal.timeout(timeoutMs),
          });
        }

        if (typeof AbortController === 'undefined') return fetch(resource, options);

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
        return fetch(resource, {
          ...options,
          signal: controller.signal,
        }).finally(() => window.clearTimeout(timeoutId));
      }

      async renderThemeCard(item, index = 0) {
        const baseUrl = item.url ? item.url.split('?')[0] : `/products/${item.handle}`;
        const localizedBaseUrl = this.localizedPath(baseUrl);
        const productUrl = this.withVariant(localizedBaseUrl, item._variant_id);
        const cacheKey = `${item.handle}::${item._variant_id || 'default'}`;

        let html = this.cardCache.get(cacheKey);
        if (!html) {
          const sectionParams = new URLSearchParams({
            section_id: 'vehicle-matcher-product-card',
          });
          if (item._variant_id) sectionParams.set('variant', item._variant_id);
          const sectionUrl = `${localizedBaseUrl}?${sectionParams.toString()}`;
          const res = await this.fetchWithTimeout(sectionUrl, {
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            cache: 'force-cache',
          });
          if (!res.ok) throw new Error(`Card render failed for ${item.handle}: ${res.status}`);
          html = await res.text();
          this.cardCache.set(cacheKey, html);
          this.trimCardCache();
        } else {
          this.cardCache.delete(cacheKey);
          this.cardCache.set(cacheKey, html);
        }

        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const card = template.content.querySelector('.product-card-wrapper');
        if (!card) return null;

        card.querySelectorAll('a[href]').forEach((link) => {
          link.href = productUrl;
        });
        this.uniquifyCardIds(card, `${this.dataset.sectionId || 'matcher'}-${item.handle}-${item._variant_id || 'default'}`);

        const itemEl = document.createElement('li');
        itemEl.className = 'grid__item vehicle-matcher__grid-item';
        itemEl.dataset.vmProductHandle = item.handle || '';
        itemEl.dataset.vmResultPosition = String(index + 1);
        itemEl.appendChild(card);
        return itemEl;
      }

      onResultClick(event) {
        const link = event.target.closest('a[href]');
        if (!link || !this.resultsEl?.contains(link)) return;

        const itemEl = link.closest('[data-vm-product-handle]');
        if (!itemEl) return;

        // Guard against double-firing if the click bubbles through nested anchors.
        if (itemEl.dataset.vmTracked === '1') return;
        itemEl.dataset.vmTracked = '1';

        const position = Number(itemEl.dataset.vmResultPosition);
        // sendBeacon + send_instantly so the click survives the same-tab
        // navigation that immediately follows (a normal capture would be
        // cancelled in-flight, undercounting clicks).
        this.track('MATCHER_PRODUCT_CLICKED', {
          ...this.currentVehicleSelection(),
          product_handle: itemEl.dataset.vmProductHandle || null,
          position: Number.isFinite(position) ? position : null,
        }, { transport: 'sendBeacon', send_instantly: true });
      }

      onUniversalClick(event) {
        const link = event.target.closest('a[data-vm-universal-option]');
        if (!link || !this.universalEl?.contains(link)) return;

        if (link.dataset.vmTracked === '1') return;
        link.dataset.vmTracked = '1';

        this.track('MATCHER_UNIVERSAL_OPTION_CLICKED', {
          ...this.currentVehicleSelection(),
          option: link.dataset.vmUniversalOption || null,
          no_result_variant: this.effectiveNoResultVariant(),
        }, { transport: 'sendBeacon', send_instantly: true });
      }

      variantIdForYear(product, year) {
        const value = product.year_variants?.[year];
        if (Array.isArray(value)) {
          return value.length > 0 ? String(value[0]) : null;
        }
        if (value && typeof value === 'object' && Array.isArray(value.ids)) {
          return value.ids.length > 0 ? String(value.ids[0]) : null;
        }
        return value ? String(value) : null;
      }

      localizedPath(path) {
        if (!path || !path.startsWith('/')) return path;
        const root = window.Shopify?.routes?.root || '/';
        if (root === '/') return path;
        return `${root.replace(/\/$/, '')}${path}`;
      }

      withVariant(url, variantId) {
        if (!variantId) return url;
        return `${url}?variant=${encodeURIComponent(variantId)}`;
      }

      uniquifyCardIds(root, suffix) {
        const cleanSuffix = String(suffix).replace(/[^A-Za-z0-9_-]/g, '-');
        const idMap = new Map();

        root.querySelectorAll('[id]').forEach((element) => {
          const oldId = element.id;
          const newId = `${oldId}-${cleanSuffix}`;
          idMap.set(oldId, newId);
          element.id = newId;
        });

        if (!idMap.size) return;

        const referenceAttributes = [
          'aria-controls',
          'aria-describedby',
          'aria-labelledby',
          'aria-owns',
          'for',
          'form',
          'list',
        ];
        const selector = referenceAttributes.map((attribute) => `[${attribute}]`).join(',');

        root.querySelectorAll(selector).forEach((element) => {
          referenceAttributes.forEach((attribute) => {
            const value = element.getAttribute(attribute);
            if (!value) return;

            const nextValue = value
              .split(/\s+/)
              .map((id) => idMap.get(id) || id)
              .join(' ');
            element.setAttribute(attribute, nextValue);
          });
        });
      }

      async mapWithConcurrency(items, limit, mapper) {
        const results = new Array(items.length);
        let nextIndex = 0;
        const workers = Array.from(
          { length: Math.min(limit, items.length) },
          async () => {
            while (nextIndex < items.length) {
              const currentIndex = nextIndex;
              nextIndex += 1;
              results[currentIndex] = await mapper(items[currentIndex], currentIndex);
            }
          },
        );
        await Promise.all(workers);
        return results;
      }

      trimCardCache() {
        while (this.cardCache.size > this.maxCardCacheEntries) {
          const oldestKey = this.cardCache.keys().next().value;
          this.cardCache.delete(oldestKey);
        }
      }

      appendOtherOption(select) {
        const opt = document.createElement('option');
        opt.value = this.otherValue;
        opt.textContent = 'Other';
        select.appendChild(opt);
      }

      isOtherValue(value) {
        return value === this.otherValue;
      }

      analyticsValue(value) {
        if (!value) return null;
        return this.isOtherValue(value) ? 'Other' : value;
      }

      currentVehicleSelection() {
        return {
          make: this.analyticsValue(this.makeEl?.value),
          model: this.analyticsValue(this.modelEl?.value),
          year: this.analyticsValue(this.yearEl?.value),
        };
      }

      emptyCategoryBreakdown() {
        return { module: 0, hu: 0, tesla: 0, other: 0 };
      }

      categoryBreakdown(items) {
        const counts = this.emptyCategoryBreakdown();
        items.forEach((item) => {
          const category = item?._cat || this.categorize(item);
          counts[category] = (counts[category] || 0) + 1;
        });
        return counts;
      }

      safePageUrl() {
        try {
          const currentUrl = new URL(window.location.href);
          const sanitizedUrl = new URL(currentUrl.origin + currentUrl.pathname);
          ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'variant'].forEach(
            (param) => {
              currentUrl.searchParams.getAll(param).forEach((value) => {
                sanitizedUrl.searchParams.append(param, value);
              });
            },
          );

          return sanitizedUrl.toString();
        } catch (error) {
          return window.location.origin + window.location.pathname;
        }
      }

      resetModel(text = 'Select model') {
        this.resetSelectPlaceholder(
          this.modelEl,
          text,
        );
        this.modelEl.disabled = true;
      }

      resetYear(text = 'Select year') {
        this.resetSelectPlaceholder(
          this.yearEl,
          text,
        );
        this.yearEl.disabled = true;
      }

      resetSelectPlaceholder(select, text) {
        select.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = text;
        placeholder.selected = true;
        placeholder.disabled = true;
        placeholder.hidden = true;
        select.appendChild(placeholder);
      }

      advanceToSelect(select) {
        if (!this.shouldAutoAdvanceSelect(select)) return;

        this.clearAutoAdvancedFocus();
        select.setAttribute('data-vm-auto-advanced', 'true');
        select.focus({ preventScroll: true });
        if (typeof select.showPicker !== 'function') return;

        try {
          select.showPicker();
        } catch {
          // Unsupported browsers or expired user activation still get focus on the next step.
        }
      }

      shouldAutoAdvanceSelect(select) {
        if (!select || select.disabled || this.lastSelectInteraction !== 'fine-pointer') {
          return false;
        }
        if (typeof window.matchMedia !== 'function') return true;

        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      }

      clearAutoAdvancedFocus(select = null) {
        const targets = select ? [select] : this.selectEls || [];
        targets.forEach((target) => target.removeAttribute('data-vm-auto-advanced'));
      }

      cancelPendingRender() {
        this.renderToken += 1;
        this.hideResults();
        return this.renderToken;
      }

      hideResults() {
        this.classList.remove('vehicle-matcher--has-results');
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
      }

      scrollToReveal(el) {
        if (!el || !this.isConnected) return;

        // Leave it alone when the outcome already sits in the top half of the
        // viewport; only scroll when it would otherwise render below the fold.
        const rect = el.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= window.innerHeight * 0.5) return;

        const reduceMotion =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion || typeof window.requestAnimationFrame !== 'function') {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
          return;
        }
        // One-frame delay so the iOS select-picker dismissal reflow (URL bar,
        // viewport resize) settles before the target position is measured.
        window.requestAnimationFrame(() => {
          if (this.isConnected) this.animateScrollTo(el);
        });
      }

      // Native smooth scrolling is browser-tuned and reads as abrupt for this
      // hop, so ease the viewport manually. User scroll input cancels it.
      animateScrollTo(el) {
        this.cancelScrollAnimation();

        const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
        const start = window.scrollY;
        const target = Math.max(0, start + el.getBoundingClientRect().top - margin);
        const distance = target - start;
        if (Math.abs(distance) < 1) return;

        const duration = 1000;
        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const state = { cancelled: false, frame: 0 };
        const cancel = () => {
          state.cancelled = true;
          window.cancelAnimationFrame(state.frame);
          window.removeEventListener('wheel', cancel);
          window.removeEventListener('touchmove', cancel);
        };
        // touchmove, not touchstart: a stray tap shouldn't kill the scroll,
        // only an actual drag/scroll gesture (wheel covers desktop).
        window.addEventListener('wheel', cancel, { passive: true, once: true });
        window.addEventListener('touchmove', cancel, { passive: true, once: true });
        this._cancelScrollAnimation = cancel;

        let startTime = null;
        const step = (now) => {
          if (state.cancelled) return;
          if (startTime === null) startTime = now;
          const progress = Math.min(1, (now - startTime) / duration);
          window.scrollTo(0, start + distance * easeInOutCubic(progress));
          if (progress < 1) {
            state.frame = window.requestAnimationFrame(step);
          } else {
            cancel();
          }
        };
        state.frame = window.requestAnimationFrame(step);
      }

      cancelScrollAnimation() {
        if (this._cancelScrollAnimation) {
          this._cancelScrollAnimation();
          this._cancelScrollAnimation = null;
        }
      }

      effectiveNoResultVariant() {
        return NO_RESULT_VARIANT === 'B' && this.universalEl ? 'B' : 'A';
      }

      showEmpty(token = this.renderToken) {
        if (token !== this.renderToken) return;

        const noResultVariant = this.effectiveNoResultVariant();
        this.track('MATCHER_NO_RESULTS', {
          ...this.currentVehicleSelection(),
          no_result_variant: noResultVariant,
        });
        this.hideResults();
        this.emptyEl.hidden = false;
        // A successful submit leaves the button disabled; re-arm it for the
        // next vehicle.
        const submit = this.emailFormEl?.querySelector('button[type="submit"]');
        if (submit) submit.disabled = false;

        if (noResultVariant === 'B') {
          this.classList.add('vehicle-matcher--has-universal');
          if (this.emailPanelEl) this.emailPanelEl.hidden = true;
          if (this.emailStatusEl) this.emailStatusEl.hidden = true;
          if (this.universalEl) this.universalEl.hidden = false;
          const selectedMake = this.analyticsValue(this.makeEl?.value);
          const selectedModel = this.analyticsValue(this.modelEl?.value);
          const selectedYear = this.analyticsValue(this.yearEl?.value);
          const makeKnown = selectedMake && selectedMake !== 'Other';
          const modelKnown = selectedModel && selectedModel !== 'Other';

          // The eyebrow carries the personalized miss at three depths:
          // unknown make, known make, or a known make+model where only the
          // chosen year has no unit. The headline is the same for all three.
          let eyebrowText = 'No exact match for your car yet';
          if (makeKnown && modelKnown && selectedYear) {
            eyebrowText = `No ${selectedMake} ${selectedModel} unit for your year yet`;
          } else if (makeKnown) {
            eyebrowText = `No ${selectedMake}-specific unit yet`;
          }

          if (this.emptyEyebrowEl) {
            this.emptyEyebrowEl.textContent = eyebrowText;
            this.emptyEyebrowEl.hidden = false;
          }
          if (this.emptyTitleEl) {
            this.emptyTitleEl.textContent = 'Our universal head units get you the same upgrade';
          }
          if (this.emptySubtitleEl) {
            this.emptySubtitleEl.textContent =
              "Our universal head unit covers most cars we don't list. Order it, send us a photo of your dash, and we confirm compatibility and the right harness before it ships.";
          }
          this.hideManualFields();
          // Scroll only after the eyebrow/title text is set so the anchored
          // height is final.
          this.scrollToReveal(this.emptyEl);
          return;
        }

        this.classList.remove('vehicle-matcher--has-universal');
        if (this.emptyEyebrowEl) this.emptyEyebrowEl.hidden = true;
        if (this.emailPanelEl) this.emailPanelEl.hidden = false;
        if (this.universalEl) this.universalEl.hidden = true;
        if (this.configureManualField()) {
          this.showManualFields();
        } else {
          this.hideManualFields();
        }
        if (this.emailStatusEl) this.emailStatusEl.hidden = true;
        this.scrollToReveal(this.emptyEl);
      }

      // What the visitor still has to type after an "Other" pick. The
      // dropdown values they did pick become a fixed prefix, so they only
      // type the missing part. Returns null when nothing is missing.
      manualFieldSpec() {
        const make = this.makeEl?.value;
        const model = this.modelEl?.value;
        const year = this.yearEl?.value;
        if (this.isOtherValue(make)) {
          return {
            scope: 'vehicle',
            prefix: '',
            label: 'Your car make, model and year',
            placeholder: 'eg 2019 Kia Sportage',
            missing: 'Enter your car make, model and year.',
          };
        }
        // "All Audi" style entries cover a whole make, so the visitor's real
        // model is still unknown: ask for model and year, not just the year.
        if (this.isOtherValue(model) || (this.isOtherValue(year) && /^All /i.test(model))) {
          return {
            scope: 'model',
            prefix: make,
            label: `Your ${make} model and year`,
            placeholder: 'Model and year',
            missing: `Enter your ${make} model and year.`,
          };
        }
        if (this.isOtherValue(year)) {
          return {
            scope: 'year',
            prefix: `${make} ${model}`,
            label: `Your ${make} ${model} year`,
            placeholder: 'eg 2009',
            missing: 'Enter your car year, eg 2009.',
          };
        }
        return null;
      }

      configureManualField() {
        const spec = this.manualFieldSpec();
        const input = this.manualVehicleInputEl;
        if (!spec || !input) return false;

        // Keep typed text while the visitor stays on the same miss; clear it
        // when the missing part changes (eg a Honda model typed, then the
        // make switched to Toyota).
        const key = `${spec.scope}::${spec.prefix}`;
        if (this.manualFieldKey !== key) input.value = '';
        this.manualFieldKey = key;

        if (this.manualPrefixEl) {
          this.manualPrefixEl.textContent = spec.prefix;
          this.manualPrefixEl.hidden = !spec.prefix;
        }
        if (this.manualLabelEl) this.manualLabelEl.textContent = spec.label;
        input.placeholder = spec.placeholder;
        if (spec.scope === 'year') {
          input.inputMode = 'numeric';
          input.maxLength = 4;
        } else {
          input.removeAttribute('inputmode');
          // The endpoint caps the combined vehicle text at 100 characters.
          input.maxLength = Math.max(20, 100 - (spec.prefix ? spec.prefix.length + 1 : 0));
        }
        return true;
      }

      captureUniversalVideoFacades() {
        // Pristine copies of the demo-video facade. Hiding a playing YouTube
        // iframe leaves its audio running, and the analytics layer's WeakSet
        // blocks re-activating the same element, so reset means swapping in a
        // fresh node rather than toggling classes.
        this.universalVideoFacades = [];
        if (!this.universalEl) return;
        this.universalEl.querySelectorAll('.cts-product-video-facade').forEach((el) => {
          this.universalVideoFacades.push({ el, html: el.outerHTML });
        });
      }

      resetUniversalVideoFacades() {
        (this.universalVideoFacades || []).forEach((entry) => {
          if (!entry.el.isConnected) return;
          if (!entry.el.classList.contains('cts-product-video-facade--loaded')) return;
          const holder = document.createElement('template');
          holder.innerHTML = entry.html.trim();
          const fresh = holder.content.firstElementChild;
          if (!fresh) return;
          entry.el.replaceWith(fresh);
          entry.el = fresh;
        });
      }

      hideEmpty() {
        this.classList.remove('vehicle-matcher--has-universal');
        this.emptyEl.hidden = true;
        this.resetUniversalVideoFacades();
        if (this.emptyEyebrowEl) {
          this.emptyEyebrowEl.hidden = true;
          this.emptyEyebrowEl.textContent = '';
        }
        if (this.emptyTitleEl) this.emptyTitleEl.textContent = this.defaultEmptyTitle;
        if (this.emptySubtitleEl) this.emptySubtitleEl.textContent = this.defaultEmptySubtitle;
        if (this.emailPanelEl) this.emailPanelEl.hidden = false;
        if (this.universalEl) {
          this.universalEl.hidden = true;
          this.universalEl.querySelectorAll('[data-vm-universal-option]').forEach((link) => {
            delete link.dataset.vmTracked;
          });
        }
        this.hideManualFields();
      }

      hideManualFields() {
        if (this.manualFieldsEl) this.manualFieldsEl.hidden = true;
        if (this.manualVehicleInputEl) {
          this.manualVehicleInputEl.disabled = true;
          this.manualVehicleInputEl.required = false;
        }
      }

      showManualFields() {
        if (!this.manualFieldsEl) return;

        this.manualFieldsEl.hidden = false;
        if (this.manualVehicleInputEl) {
          this.manualVehicleInputEl.disabled = false;
          this.manualVehicleInputEl.required = true;
          window.setTimeout(() => {
            // preventScroll so the focus jump doesn't fight showEmpty's smooth
            // scroll; the container scroll already brings this into view.
            this.manualVehicleInputEl.focus({ preventScroll: true });
          }, 0);
        }
      }

      async onEmailSubmit(e) {
        e.preventDefault();
        const input = this.emailFormEl.querySelector('input[name="email"]');
        const email = (input?.value || '').trim();
        const makeValue = this.makeEl.value;
        const modelValue = this.modelEl.value;
        const yearValue = this.yearEl.value;
        const makeIsOther = this.isOtherValue(makeValue);
        const modelIsOther = this.isOtherValue(modelValue);
        const yearIsOther = this.isOtherValue(yearValue);
        const hasOtherSelection = makeIsOther || modelIsOther || yearIsOther;
        const manualSpec = hasOtherSelection ? this.manualFieldSpec() : null;
        const manualText = (this.manualVehicleInputEl?.value || '').trim().replace(/\s+/g, ' ');
        const manualVehicle =
          manualText && manualSpec?.prefix ? `${manualSpec.prefix} ${manualText}` : manualText;
        const otherSelection = {
          make: makeIsOther,
          model: modelIsOther,
          year: yearIsOther,
        };
        const make = makeIsOther ? 'Other' : makeValue;
        const model = makeIsOther ? null : modelIsOther ? 'Other' : modelValue;
        const year = hasOtherSelection ? null : yearValue;

        if (hasOtherSelection && !manualText) {
          this.setEmailStatus(manualSpec?.missing || 'Enter your car make, model and year.', 'error');
          this.manualVehicleInputEl?.focus();
          return;
        }
        if (manualSpec?.scope === 'year') {
          const typedYear = Number(manualText);
          const maxYear = new Date().getFullYear() + 1;
          if (!/^\d{4}$/.test(manualText) || typedYear < 1950 || typedYear > maxYear) {
            this.setEmailStatus(`Enter a year between 1950 and ${maxYear}.`, 'error');
            this.manualVehicleInputEl?.focus();
            return;
          }
        }
        if (!hasOtherSelection && (!make || !model || !year)) {
          this.setEmailStatus('Pick make, model, and year first.', 'error');
          return;
        }
        if (!email) {
          this.setEmailStatus('Please enter your email.', 'error');
          return;
        }
        if (input && !input.validity.valid) {
          this.setEmailStatus('Enter a valid email address.', 'error');
          return;
        }
        if (!this.apiUrl) {
          this.setEmailStatus('Email signup is not configured.', 'error');
          return;
        }

        const submit = this.emailFormEl.querySelector('button[type="submit"]');
        if (submit) submit.disabled = true;
        this.setEmailStatus('Saving...', 'pending');
        // Any dropdown change bumps renderToken. If the visitor moved to
        // another vehicle while this request was in flight, the response must
        // not overwrite the new vehicle's form state.
        const submitToken = this.renderToken;
        const isStale = () => submitToken !== this.renderToken;

        const trackingProps = {
          make,
          model,
          year,
          other_selection: otherSelection,
          has_manual_vehicle: Boolean(manualVehicle),
        };

        try {
          this.track('MATCHER_EMAIL_SUBMIT_ATTEMPTED', trackingProps);
          const res = await this.fetchWithTimeout(this.apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              year: year == null ? null : Number(year),
              make,
              model,
              other_selection: otherSelection,
              manual_vehicle: manualVehicle || null,
              email,
              source_url: this.safePageUrl(),
            }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok || !json.ok) {
            this.track('MATCHER_EMAIL_SUBMIT_FAILED', {
              ...trackingProps,
              reason: json.error || `http_${res.status}`,
            });
            if (isStale()) return;
            this.setEmailStatus(
              json.error || 'Something went wrong. Try again later.',
              'error',
            );
            if (submit) submit.disabled = false;
            return;
          }
          this.track('MATCHER_EMAIL_SUBMITTED', trackingProps);
          if (isStale()) return;
          this.setEmailStatus(
            "Got it. We'll check this vehicle and follow up by email.",
            'success',
          );
          input.value = '';
          if (this.manualVehicleInputEl) this.manualVehicleInputEl.value = '';
        } catch (err) {
          console.error('[vehicle-matcher] email submit error', err);
          this.track('MATCHER_EMAIL_SUBMIT_FAILED', {
            ...trackingProps,
            reason: err?.name || 'network_error',
          });
          if (isStale()) return;
          this.setEmailStatus('Network error. Try again.', 'error');
          if (submit) submit.disabled = false;
        }
      }

      setEmailStatus(text, kind) {
        if (!this.emailStatusEl) return;
        this.emailStatusEl.textContent = text;
        this.emailStatusEl.dataset.state = kind;
        this.emailStatusEl.hidden = false;
      }
    },
  );
}
